<template>
  <div class="row">
    <div class="col-12">
      <LicensesTable :show-action="false" :licenses="licenses">
        <template #appName="slotprops">
          <div>
            <div class="row g-0">
              <div class="col-12">
                <h6
                  class="pointer"
                  role="link"
                  tabindex="0"
                  @keydown.enter="goToAppPage(slotprops.row.item)"
                  @click="goToAppPage(slotprops.row.item)"
                >
                  <strong>{{ slotprops.row.item.appName }}</strong>
                </h6>
              </div>
            </div>
            <div class="row g-0">
              <div class="col-12">
                <div class="tooltip-container">
                  <p>{{ getAppTypeName(slotprops.row.item) }}</p>
                </div>
              </div>
            </div>
          </div>

          <div class="row g-0 mt-2">
            <div class="col-12 col-lg-9">
              <h6>Licenses</h6>

              <div class="tooltip-container">
                <p class="mb-0">
                  Mark license for install
                  <span
                    v-if="
                      slotprops.row.item.appSettings?.licenseVariantsAllowed &&
                      !slotprops.row.item.appSettings
                        ?.multiLicenseVariantSelection
                    "
                  >
                    |
                    <span
                      :class="
                        getSelectedLicenseIds(slotprops.row.item).length > 0
                          ? 'small font-phoenix-cyan pointer'
                          : 'small'
                      "
                      tabindex="0"
                      role="button"
                      @click.stop="clearSelectedLicenses(slotprops.row.item)"
                      @keydown.enter="clearSelectedLicenses(slotprops.row.item)"
                    >
                      Clear
                    </span>
                  </span>
                </p>
              </div>

              <BTable
                hover
                small
                responsive
                thead-class="d-none"
                borderless
                :sort-desc="true"
                :fields="licenseOptionTableFields"
                :items="
                  getGroupByLicenseOptionAndClientCount(slotprops.row.item)
                "
              >
                <template #cell(checkbox)="data">
                  <div v-if="data.item.appSettings?.licenseVariantsAllowed">
                    <div
                      v-if="data.item.appSettings?.multiLicenseVariantSelection"
                    >
                      <BFormCheckbox
                        v-model="
                          selectedLicenseIdsByAppId[slotprops.row.item.appId]
                        "
                        size="sm"
                        :value="data.item.id"
                      />
                    </div>

                    <div v-else>
                      <BFormRadio
                        :name="`license-option-${slotprops.row.item.appId}`"
                        :model-value="
                          (selectedLicenseIdsByAppId[
                            slotprops.row.item.appId
                          ] ?? [])[0] ?? null
                        "
                        size="sm"
                        :value="data.item.id"
                        @update:model-value="
                          (v) => {
                            selectedLicenseIdsByAppId[
                              slotprops.row.item.appId
                            ] = typeof v === 'number' ? [v] : [];
                          }
                        "
                      />
                    </div>
                  </div>

                  <div v-else>
                    <BFormCheckbox
                      v-model="
                        selectedLicenseIdsByAppId[slotprops.row.item.appId]
                      "
                      size="sm"
                      :value="data.item.id"
                    />
                  </div>
                </template>

                <template #cell(name)="data">
                  <div class="tooltip-container row g-0">
                    <div class="col-md-10 col-12">
                      <span>{{
                        data.item.licenseOption?.name ?? "Unknown"
                      }}</span>
                      <div
                        v-if="
                          getBundleComponents(data.item.licenseOption).length >
                          0
                        "
                      >
                        <p class="mb-0">
                          (Also included:
                          <span
                            v-for="(
                              bundleComponent, index
                            ) in getBundleComponents(
                              data.item.licenseOption,
                            ).sort(sortBy({ key: 'name', order: 'asc' }))"
                            :key="bundleComponent.id"
                          >
                            {{ bundleComponent.name
                            }}{{
                              index <
                              getBundleComponents(data.item.licenseOption)
                                .length -
                                1
                                ? ", "
                                : ""
                            }}
                          </span>
                          )
                        </p>
                      </div>
                    </div>
                  </div>
                </template>

                <template #cell(available)="data">
                  <div
                    class="tooltip-container d-inline-block justify-content-end"
                  >
                    <span class="light-gray">
                      {{ licensesLeft(data.item) }} license(s) available
                    </span>
                    <br />
                    <span v-if="data.item.clientCount !== 1" class="light-gray">
                      {{ data.item.clientCount }} Clients
                      <HelpBadgeCircle
                        class="d-inline"
                        tip-html="This license supports multiple clients (e.g. for a network license server)."
                      />
                    </span>
                  </div>
                </template>

                <template #cell(actions)="data">
                  <div class="tooltip-container d-flex justify-content-end">
                    <span
                      class="small font-phoenix-cyan pointer"
                      tabindex="0"
                      role="button"
                      @click.stop="moveToCompany(data.item)"
                      @keydown.enter="moveToCompany(data.item)"
                    >
                      Move
                    </span>
                  </div>
                </template>
              </BTable>
            </div>
          </div>
        </template>

        <template #buttons>
          <StoreButton
            :disabled="isInstallBtnDisabled()"
            variant="primary"
            class="mt-2 mt-md-0 w-md-auto"
            @click="handleInstallAllSelectedLicenses"
          >
            Install {{ selectedLicenseOptionIds.length }} licenses
          </StoreButton>
        </template>

        <template #empty>
          <div>
            <span>No License found.</span>
          </div>
        </template>
      </LicensesTable>
    </div>
  </div>
</template>
<script setup lang="ts">
import { type GroupedLicenses, useLicensesStore } from "~/stores/licensesstore";
import LicensesTable from "~/components/specific/licenses/LicensesTable.vue";
import groupBy from "~/helpers/groupBy";
import HelpBadgeCircle from "~/components/shared/buttons/HelpBadgeCircle.vue";
import type {
  WebModelGetAppLicenseOption,
  WebModelGetAppSettings,
  WebModelGetLicense,
} from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { useToastStore } from "~/stores/ui/toaststore";
import { sortBy } from "~/helpers/sortBy";
import { useGoToAppPage } from "~/mixins/useGoToAppPage";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import type { PropType } from "vue";

const licensesStore = useLicensesStore();
const licenses = computed(
  () => licensesStore.getGroupedLicensesForMyLicensePool,
);

type BundleComponent = { id: string | number; name: string };
type LicenseOptionWithBundleComponents = WebModelGetAppLicenseOption & {
  bundleComponents?: BundleComponent[];
};

const getBundleComponents = (
  option?: WebModelGetAppLicenseOption | null,
): BundleComponent[] => {
  return (
    (option as LicenseOptionWithBundleComponents | null | undefined)
      ?.bundleComponents ?? []
  );
};

const props = defineProps({
  handleInstall: {
    type: Function as PropType<
      (
        appSettings: WebModelGetAppSettings | undefined,
        selectedLicenses: WebModelGetLicense[],
        selectedAppVersionId: number | undefined,
      ) => void
    >,
    required: true,
  },
});

/**
 * Per-app selection:
 * Key: appId
 * Value: selected license option ids (array; for radio/single-select it will contain 0 or 1 element)
 */
const selectedLicenseIdsByAppId = reactive<Record<string, number[]>>({});

watch(
  licenses,
  (newLicenses) => {
    for (const app of newLicenses ?? []) {
      if (!app?.appId) continue;
      if (!selectedLicenseIdsByAppId[app.appId])
        selectedLicenseIdsByAppId[app.appId] = [];
    }
  },
  { immediate: true },
);

/** Flattened list across all apps (what you show in the top button counter). */
const selectedLicenseOptionIds = computed<number[]>(() =>
  Object.values(selectedLicenseIdsByAppId).flat(),
);

const getSelectedLicenseIds = (appLicense: GroupedLicenses): number[] => {
  if (!appLicense?.appId) return [];
  return selectedLicenseIdsByAppId[appLicense.appId] ?? [];
};

const clearSelectedLicenses = (appLicense: GroupedLicenses) => {
  if (!appLicense?.appId) return;
  selectedLicenseIdsByAppId[appLicense.appId] = [];
};

const isInstallBtnDisabled = () => {
  return selectedLicenseOptionIds.value.length < 1;
};

const licenseOptionTableFields = [
  { key: "checkbox", label: "", tdClass: "w-10" },
  { key: "name", label: "Name", tdClass: "w-50" },
  { key: "available", label: "Available", tdClass: "w-25" },
  { key: "actions", label: "", tdClass: "w-15" },
];

const getGroupByLicenseOptionAndClientCount = (
  appLicense: GroupedLicenses,
): GroupedLicenses[] => {
  if (!appLicense?.allLicenses || appLicense.allLicenses.length < 1) return [];

  return groupBy(appLicense.allLicenses, (item) => {
    const optionId = item.licenseOption?.id ?? "no-license-option";
    const clientCount = item.clientCount ?? "no-client-count";
    return `licenseOptionId:${optionId}|clientCount:${clientCount}`;
  }).sort(
    sortBy(
      { key: "licenseOption.name", order: "asc" },
      { key: "clientCount", order: "asc" },
    ),
  );
};

const goToAppPage = async (appLicense: GroupedLicenses) => {
  await useGoToAppPage().goToAppPage(appLicense);
};

const moveToCompany = async (licenseGroup: GroupedLicenses) => {
  console.debug("moveToCompany", licenseGroup.licenseOption?.name);
  if (licenseGroup.id) {
    const success = await licensesStore.moveLicenseToCompany(licenseGroup.id);
    if (success) {
      useToastStore().addToastWithMessageAndTitle(
        "Success",
        "License moved to company",
        "success",
      );
    }
  }
};

const getAppTypeName = (groupedLicenses: GroupedLicenses) => {
  return groupedLicenses.appSettings?.appType?.name ?? "Unknown";
};

const licensesLeft = (licenseGroup: GroupedLicenses) => {
  // In the old UI this counted "left" from the (filtered) pool.
  // In this grouped view, the group already represents "available" items.
  return licenseGroup.allLicenses?.length ?? 0;
};

const handleInstallAllSelectedLicenses = () => {
  const selectedLicenses: WebModelGetLicense[] = [];
  licensesStore.userLicenses.find((license) => {
    if (license && license.id) {
      if (selectedLicenseOptionIds.value.includes(license.id)) {
        selectedLicenses.push(license);
      }
    }
  });

  props.handleInstall(undefined, selectedLicenses, undefined);
};
</script>

<style scoped lang="scss">
@use "@/sass/components/LicenseTable.scss" as *;
</style>
